import type { FeedbackAnswerQuestion } from '@repo/types/questions/Question';

import { useGetQuizMarked } from '@repo/api/quiz/useGetQuizMarked';
import { useEffect } from 'react';

import { useLocalStorage } from '@/hooks/local-storage';

/**
 * Whether a marked draft has the same section/question layout as the attempt
 * loaded from the server. A mismatch means the draft belongs to a different
 * quiz/attempt (or the quiz was edited since), and the backend would reject it
 * with "Wrong answer count for section idx N".
 */
function matchesAttemptShape(draft: FeedbackAnswerQuestion[][] | null, server: FeedbackAnswerQuestion[][] | null | undefined): boolean {
  if (!draft || !server) return false;
  if (draft.length !== server.length) return false;
  return draft.every((section, idx) => section.length === server[idx]?.length);
}

export function useQuizMarkedData(quizId?: string, attemptId?: string) {
  const quizMarkedQuery = useGetQuizMarked(quizId, attemptId);
  const { data, isSuccess } = quizMarkedQuery;
  // Scope the draft per attempt so a draft started on one quiz can never leak
  // into another (the shared 'quiz-marked' key caused cross-attempt bleed).
  const storageKey = `quiz-marked-${quizId ?? 'unknown'}-${attemptId ?? 'unknown'}`;
  const [markedData, setMarkedData, removeMarkedData] = useLocalStorage<FeedbackAnswerQuestion[][] | null>(storageKey, null);

  const updateMarkedQuestion = (marked: Partial<Omit<FeedbackAnswerQuestion, 'id'>>, sectionIdx: number, questionIdx: number) => {
    setMarkedData((prev) => {
      const storedData: FeedbackAnswerQuestion[][] | undefined = prev ?? quizMarkedQuery.data?.attempt.body.marked;
      if (!storedData) return null;
      const newData: FeedbackAnswerQuestion[][] = [...storedData];

      if (newData[sectionIdx]?.[questionIdx]) {
        newData[sectionIdx][questionIdx] = {
          ...newData[sectionIdx][questionIdx],
          ...marked,
        };
      }
      return newData;
    });
  };

  const getMarkedQuestionData = (sectionIdx: number, questionIdx: number): FeedbackAnswerQuestion | undefined => {
    const storedData: FeedbackAnswerQuestion[][] | undefined = markedData ?? data?.attempt.body.marked;

    if (!storedData) return undefined;
    return storedData[sectionIdx]?.[questionIdx];
  };

  useEffect(() => {
    if (!isSuccess) return;
    const serverMarked = data.attempt.body.marked ?? null;
    // Seed an empty draft from the server, and replace any stale draft whose
    // shape no longer matches the loaded attempt so we never submit a payload
    // the backend would reject.
    if (markedData === null) {
      setMarkedData(serverMarked);
    } else if (serverMarked && !matchesAttemptShape(markedData, serverMarked)) {
      setMarkedData(serverMarked);
    }
  }, [data, markedData, isSuccess]);

  return {
    quizMarkedQuery,
    markedData,
    getMarkedQuestionData,
    updateMarkedQuestion,
    removeMarkedData,
  };
}
